import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LogsService } from '../services/logs.service';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatIcon } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { BufferProgressBarComponent } from '../../../ui-components/buffer-progress-bar/buffer-progress-bar.component';
import { AceEditorModule } from 'ngx-ace-editor-wrapper';
import { FilterStateService } from '../filters-dialog/filter-state.service';
import { LogTransferService } from '../services/log-transfer.service';
import { LogRequest, LogResponse } from '../../../protos/generated/admin.pb';

@Component({
    selector: 'app-log-details',
    standalone: true,
    imports: [
        MatButtonModule,
        MatDialogModule,
        MatProgressSpinnerModule,
        BufferProgressBarComponent,
        CommonModule,
        MatCardModule,
        RouterLink,
        ClipboardModule,
        MatIcon,
        MatTooltipModule,
        AceEditorModule
    ],
    templateUrl: './log-details.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./log-details.component.scss']
})
export class LogDetailsComponent implements OnInit {
    logDetails: LogResponse = new LogResponse();
    isLoading = false;
    isLogEventLoading = true;
    filtersFromQuery: any = {};

    constructor(
        private route: ActivatedRoute,
        private logsService: LogsService,
        private router: Router,
        private filterStateService: FilterStateService,
        private logTransferService: LogTransferService
    ) {}

    ngOnInit(): void {
        const logFromService = this.logTransferService.getLog();

        this.route.queryParams.subscribe(params => {
            this.filtersFromQuery = { ...params };
        });

        this.route.params.subscribe(params => {
            const logId = +params['id'];

            if (logFromService) {
                this.logDetails = logFromService;
                this.isLogEventLoading = false;
            } else {
                this.fetchLogs(logId);
            }
        });
    }

    fetchLogs(logId: number): void {
        this.isLoading = true;
        const logRequest = new LogRequest();
        logRequest.id = logId;

        this.logsService.getLogs(logRequest).subscribe({
            next: (response: { logs: LogResponse[]; total: number }) => {
                if (response.logs.length > 0) {
                    this.logDetails = response.logs[0];
                    setTimeout(() => {
                        this.isLogEventLoading = false;
                    }, 0);
                }
                this.isLoading = false;
            },
            error: err => {
                console.error('Error fetching logs:', err);
                this.isLoading = false;
                this.isLogEventLoading = false;
            }
        });
    }

    formattedJson(raw: any): string {
        if (!raw) return '';
        try {
            return JSON.stringify(
                typeof raw === 'string' ? JSON.parse(raw) : raw,
                null,
                2
            );
        } catch {
            return typeof raw === 'string' ? raw : JSON.stringify(raw);
        }
    }

    getSeverityColor(severity: string): string {
        switch (severity) {
            case 'Debug':
                return '#90CAF9';
            case 'Information':
                return '#42A5F5';
            case 'Warning':
                return '#FFC107';
            case 'Error':
                return '#F44336';
            case 'Fatal':
                return '#D32F2F';
            default:
                return '#B0BEC5';
        }
    }

    copyToClipboard(logId: number, tooltip: any): void {
        const baseUrl = `${window.location.origin}/others/developer-logs/${logId}`;
        const markdownLink = `[#${logId}](${baseUrl})`;
        navigator.clipboard
            .writeText(markdownLink)
            .then(() => {
                tooltip.disabled = false;
                tooltip.show();
                setTimeout(() => tooltip.hide(), 2000);
            })
            .catch(err => {
                console.error('Clipboard copy failed:', err);
            });
    }
    copyLogEvent(logEvent: any, tooltip: any): void {
        const text =
            typeof logEvent === 'string'
                ? logEvent
                : JSON.stringify(logEvent, null, 2);
        navigator.clipboard.writeText(text).then(() => {
            tooltip.disabled = false;
            tooltip.show();
            setTimeout(() => tooltip.hide(), 1000);
        });
    }
    goBackWithFilters(): void {
        const queryParams = this.filterStateService.getFilterQueryParams();
        this.router.navigate(['/others/developer-logs'], { queryParams });
    }
}
